import React, {useEffect, useState} from 'react';
import {Alert, Button, FormControl} from 'react-bootstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ConfirmationDialog} from '../../Common/ConfirmationDialog';
import {service as SurveySubmissionService} from '../../../Services/SurveySubmissionService';
import {service as SurveyTypeService} from '../../../Services/SurveyTypeService';
import {service as UserService} from '../../../Services/UserService';
import {usePrimeCache, useFetchServiceModel} from '../../../Hooks/ServiceHooks';
import {useChangeTool} from '../../../Hooks/RouteHooks';
import {useDefaultErrorHandler} from '../../../Hooks/ErrorHooks';
import {BOUNDARY_CODE} from '../../../Hooks/SubmissionBoundaryHooks';
import {useOrderedSubmissionStatuses, useSubmissionStatusPresentation} from '../../../Utils/surveyTagUtils';
import {PRE_SUBMIT_STATUS_CODES, deriveSubmissionActions, deriveSubmissionDisplay, hasSubmissionActionPanel} from '../../../Utils/surveySubmissionUtils';

// Maps status code → [prefix, iconName] for FontAwesome Pro
const STATUS_ICONS = {
    'not-started':     ['fal', 'circle'],
    'in-progress':     ['fal', 'pencil'],
    'ready-to-submit': ['fal', 'check-circle'],
    'in-review':       ['fas', 'eye'],
    'review-complete': ['fas', 'check-circle'],
};

// Notes-callout wording keyed by the transition recorded on the current
// submission row. Rows from before transitionAction shipped carry null and
// fall back to the status-based guess, which is right for a reopen and a
// return and wrong only for a withdrawal. A recorded action with no entry
// here (submitted / accepted) suppresses the callout instead — those notes
// aren't a request for the viewer to act on.
const NOTES_CALLOUT_LABELS = {
    reopened:  'Review reopened',
    returned:  'Changes requested',
    withdrawn: 'Submission withdrawn',
};

/**
 * SurveySubmissionWidget — shows the current submission status, a stepper,
 * progress counter, and role-appropriate action buttons.
 *
 * Props:
 *   conservationAction  — full CA object
 *   surveyTypeId        — int
 *   survey              — survey object (needs SurveySections)
 *   answers             — the page's answer array, edits and load-time writes included
 *   savedAnswers        — the answers as the server last returned them; what the
 *                         stepper and the Submit button are derived from
 *   editMode            — bool; hides action buttons when true (passive mode)
 *   canReviewSubmissions — bool; gates TFT-only actions
 *   onEnterEditMode     — () => void; called when user clicks "Edit Survey". Null
 *                         when the viewer cannot edit, which also withholds Submit
 *   onReloadData        — () => Promise; called after a successful transition
 *   submissionRefreshKey — incremented by parent after answer saves to trigger re-fetch
 */
export const SurveySubmissionWidget = (props) => {

    const {
        conservationAction,
        surveyTypeId,
        survey,
        answers,
        savedAnswers,
        editMode,
        canReviewSubmissions,
        onEnterEditMode,
        onReloadData,
        onSubmissionChange,
        isSectionComplete,
        submissionRefreshKey,
    } = props;

    const changeTool = useChangeTool();
    const defaultErrorHandler = useDefaultErrorHandler();

    usePrimeCache([SurveyTypeService]);

    const presentStatus = useSubmissionStatusPresentation(canReviewSubmissions);
    const { statuses: orderedStatuses, indexOf: statusIndexOf } = useOrderedSubmissionStatuses();

    const [submission, setSubmission] = useState(undefined); // undefined = not yet fetched
    const [prerequisiteSubmission, setPrerequisiteSubmission] = useState(undefined); // undefined = not yet fetched
    const [showSubmitDialog, setShowSubmitDialog] = useState(false);
    const [showAcceptDialog, setShowAcceptDialog] = useState(false);
    const [showUnsubmitDialog, setShowUnsubmitDialog] = useState(false);
    const [unsubmitNotes, setUnsubmitNotes] = useState('');

    const conservationActionId = conservationAction?.conservationActionId;

    // --- Section completion (above-boundary sections only) ---
    // isSectionComplete is passed from SurveyComponent so custom sections
    // (RiskAssessments, PlantQuantities, etc.) use their proper completion logic.

    const sections = (survey?.SurveySections ?? [])
        .slice()
        .sort((a, b) => Number(a.order) - Number(b.order));
    const boundaryIdx = sections.findIndex(s => s.code === BOUNDARY_CODE);
    const requiredSections = boundaryIdx >= 0 ? sections.slice(0, boundaryIdx) : sections;

    // --- Status data ---
    // orderedStatuses is empty until the status cache primes, which also keeps
    // the stepper unrendered until then.

    const currentStatusCode = submission?.SurveySubmissionStatus?.code ?? 'not-started';

    // The progress counter follows the page's answers, so it tracks edits as they
    // happen. The stepper and the action buttons follow the SAVED answers: only the
    // answers the server holds decide whether a submit would succeed, and the page's
    // answers run ahead of them before anyone edits (the contact input selects a
    // project contact on load). Custom-section data (plant quantities, risks) has no
    // saved twin and is read live by both counts.
    const { completedCount, totalCount, hasUnsavedChanges, displayStatusCode, displayStatusIdx } = deriveSubmissionDisplay({
        currentStatusCode,
        hasSubmissionRecord: !!submission?.surveySubmissionId,
        requiredSections,
        isSectionComplete,
        answers,
        savedAnswers,
        indexOf: statusIndexOf,
    });

    // --- Phase-ordered survey types (for next-phase navigation and prerequisite nudge) ---
    // TODO SB-3207: Re-evaluate this logic if feature is extended to non-irrigation surveys.
    const { prev: prevSurveyType, next: nextSurveyType } = SurveyTypeService.getSurroundingSurveyTypes(surveyTypeId);

    const hasSubmissionBoundary = boundaryIdx >= 0;

    // --- Unsubmit notes callout ---

    // Show the banner whenever the row that produced the current status carries a
    // note, including to the person who wrote it: the banner is the standing
    // explanation of why the survey sits where it does, not a notification, so
    // the author needs it as much as anyone when they come back to the survey
    // later. Naming the actor rather than addressing them in second person keeps
    // the wording identical to the feed's for the same transition.
    const showNotesCallout =
        submission?.notes &&
        submission?.changedByUserId != null &&
        (submission?.transitionAction == null || NOTES_CALLOUT_LABELS[submission.transitionAction] != null);

    const notesCalloutLabel = NOTES_CALLOUT_LABELS[submission?.transitionAction]
        ?? (currentStatusCode === 'in-review' ? 'Review reopened' : 'Changes requested');

    // Resolve the changer via the segmentation-aware single-user read (FindById ->
    // GET /users/{id} -> UserService::getUser, which applies the viewer's data
    // segmentation): a reviewer who skips segmentation sees a cross-org supplier's
    // real name; a viewer who can't see them resolves null and the banner renders
    // without a name. Segmentation is entirely server-side (UserService sends no
    // orgId); the sync CachedTitleById only read a lazily-populated cache, so it
    // missed changers that hadn't been fetched and mislabeled them "Reviewer".
    const changer = useFetchServiceModel(UserService, showNotesCallout ? submission.changedByUserId : null);
    const changerName = changer?.name ?? null;

    // --- Load current submission ---
    // StreamBank convention: prefer async/await with try/catch over .then/.catch chains.

    const fetchSubmission = async () => {
        if (!conservationActionId || !surveyTypeId) return;
        try {
            const r = await SurveySubmissionService.GetCurrent(conservationActionId, surveyTypeId);
            const result = r?.result ?? null;
            setSubmission(result);
            onSubmissionChange?.(result);
        } catch {
            setSubmission(null);
            onSubmissionChange?.(null);
        }
    };

    useEffect(() => {
        void fetchSubmission();
    }, [conservationActionId, surveyTypeId, editMode, submissionRefreshKey]);

    // Fetch the prerequisite survey's submission to determine whether the nudge should show.
    // Only fetches when there is a prev phase survey and this survey has a boundary.
    useEffect(() => {
        if (!prevSurveyType || !conservationActionId || !hasSubmissionBoundary) {
            setPrerequisiteSubmission(null);
            return;
        }
        const fetchPrerequisite = async () => {
            try {
                const r = await SurveySubmissionService.GetCurrent(conservationActionId, prevSurveyType.surveyTypeId);
                setPrerequisiteSubmission(r?.result ?? null);
            } catch {
                setPrerequisiteSubmission(null);
            }
        };
        void fetchPrerequisite();
    }, [conservationActionId, prevSurveyType?.surveyTypeId, hasSubmissionBoundary]);

    // --- Transition handlers ---

    const doSubmit = async () => {
        setShowSubmitDialog(false);
        try {
            await SurveySubmissionService.Submit({ conservationActionId, surveyTypeId });
            await Promise.all([onReloadData?.(), fetchSubmission()]);
        } catch (e) { defaultErrorHandler(e); }
    };

    const doUnsubmit = async () => {
        setShowUnsubmitDialog(false);
        // Notes are required on every unsubmit path; the confirm button is
        // disabled until they're non-empty, so this is never blank here.
        const notes = unsubmitNotes.trim();
        try {
            await SurveySubmissionService.Unsubmit({ conservationActionId, surveyTypeId, notes });
            setUnsubmitNotes('');
            await Promise.all([onReloadData?.(), fetchSubmission()]);
        } catch (e) { defaultErrorHandler(e); }
    };

    const doAccept = async () => {
        setShowAcceptDialog(false);
        try {
            await SurveySubmissionService.Accept({ conservationActionId, surveyTypeId });
            await Promise.all([onReloadData?.(), fetchSubmission()]);
        } catch (e) { defaultErrorHandler(e); }
    };

    // Don't render until submission is fetched
    if (submission === undefined) return null;

    // Surveys without a submission boundary don't use the submission workflow
    if (!hasSubmissionBoundary) return null;

    const preSubmitStatus = PRE_SUBMIT_STATUS_CODES.has(currentStatusCode);

    // Has this survey defined a supplier/reviewer boundary, and is there a previous-phase survey?
    // TFT users (canReviewSubmissions) are exempt from prerequisites — they can edit in any order.
    const hasPrerequisite = hasSubmissionBoundary && prevSurveyType !== null && !canReviewSubmissions;

    // Is the prerequisite fulfilled? undefined = still loading, null = no submission (not-started).
    // undefined?.SurveySubmissionStatus?.code evaluates to undefined !== 'review-complete',
    // so while loading we conservatively treat the prerequisite as unfulfilled. No explicit
    // undefined check needed — this is intentional: block until we know for sure it's met.
    const prerequisiteIsFulfilled =
        prerequisiteSubmission?.SurveySubmissionStatus?.code === 'review-complete';

    const isPrerequisiteBlocked = hasPrerequisite && !prerequisiteIsFulfilled;

    // What the pre-submit action panel offers, decided from the displayed status
    // and whether the viewer may edit — see deriveSubmissionActions for the rules.
    const actions = deriveSubmissionActions({
        displayStatusCode,
        hasUnsavedChanges,
        canEdit: !!onEnterEditMode,
        prerequisiteBlocked: isPrerequisiteBlocked,
    });
    const { showProgress, editAction, submitAction, showUnsavedHint, showPrerequisiteNudge } = actions;

    // Whether the action column has anything to show. When it doesn't (a viewer
    // who cannot edit, on a pre-submit survey) it is left out altogether and the
    // stepper takes the card — see hasSubmissionActionPanel for the rule.
    const hasActionPanel = hasSubmissionActionPanel({ editMode, currentStatusCode, totalCount, actions });

    return (
        <div className="sb-submission-widget">
            {/* --- Dialogs --- */}
            <ConfirmationDialog
                Title="Submit Survey"
                Display={showSubmitDialog}
                ConfirmationButtonMessage="Submit"
                CancelButtonMessage="Cancel"
                OnConfirm={doSubmit}
                OnClose={() => setShowSubmitDialog(false)}
            >
                <p>Are you sure you're ready to submit this survey for review?<br/>
                   During the review process you won't be able to make further changes.
                </p>
            </ConfirmationDialog>
            <ConfirmationDialog
                Title="Accept Survey"
                Display={showAcceptDialog}
                ConfirmationButtonMessage="Accept"
                CancelButtonMessage={"Cancel"}
                OnConfirm={doAccept}
                OnClose={() => setShowAcceptDialog(false)}
            >
                Mark this survey as review complete?
            </ConfirmationDialog>
            <ConfirmationDialog
                Title={currentStatusCode === 'review-complete' ? 'Reopen Review' : canReviewSubmissions ? 'Return Survey' : 'Withdraw Submission'}
                Display={showUnsubmitDialog}
                ConfirmationButtonMessage={currentStatusCode === 'review-complete' ? 'Reopen Review' : canReviewSubmissions ? 'Return Survey' : 'Withdraw'}
                CancelButtonMessage="Cancel"
                DisableConfirmationButton={!unsubmitNotes.trim()}
                OnConfirm={doUnsubmit}
                OnClose={() => { setShowUnsubmitDialog(false); setUnsubmitNotes(''); }}
            >
                {/* Notes are required on every unsubmit path (the API rejects empty notes). */}
                {currentStatusCode === 'review-complete' ? (
                    <>
                        <p>Reopen this survey for further review? The survey will return to in-review status.</p>
                        <p>Describe why the review is being reopened. This will be recorded in the
                           survey's history and may be visible to the supplier.</p>
                    </>
                ) : canReviewSubmissions ? (
                    <p>Describe what changes are needed. The supplier will see this message.</p>
                ) : (
                    <>
                        <p>Are you sure you want to withdraw this submission?<br/>
                           Withdrawing will allow you to make changes and resubmit,
                           but the review process will be halted until resubmitted.
                        </p>
                        <p>Describe why you're withdrawing this submission. Reviewers will see this message.</p>
                    </>
                )}
                <FormControl
                    as="textarea"
                    rows={3}
                    value={unsubmitNotes}
                    onChange={e => setUnsubmitNotes(e.target.value)}
                    placeholder={
                        currentStatusCode === 'review-complete' ? 'Describe why the review is being reopened...'
                        : canReviewSubmissions ? 'Describe what changes are needed...'
                        : 'Describe why you\'re withdrawing this submission...'
                    }
                />
            </ConfirmationDialog>

            {/* --- Notes callout (feedback left on the current submission row) --- */}
            {showNotesCallout && (
                <Alert variant="warning" className="sb-submission-notes-callout">
                    <strong>
                        {notesCalloutLabel}{changerName ? ` by ${changerName}` : ''}:
                    </strong> {submission.notes}
                </Alert>
            )}

            {/* --- Card: stepper + action panel side by side --- */}
            <div className="sb-submission-card">
                {/* Stepper */}
                <div className="sb-submission-stepper-col">
                    <div className="sb-submission-stepper">
                        {orderedStatuses.map((status, statusIdx) => {
                            const isActive = status.code === displayStatusCode;
                            const isComplete = statusIdx < displayStatusIdx;
                            // Each node's label comes from the shared presentation resolver, so
                            // the stepper and the project-list badge agree. The note belongs to
                            // the current submission, so only the active node can resolve to
                            // "Changes Requested"; the rest get their plain phase label.
                            const { label: stepLabel } = presentStatus(
                                status.code,
                                status.code === currentStatusCode && !!submission?.notes,
                            ) ?? {};
                            return (
                                <div
                                    key={status.surveySubmissionStatusId}
                                    className={[
                                        'sb-submission-step',
                                        isActive ? 'active' : '',
                                        isComplete ? 'complete' : '',
                                    ].filter(Boolean).join(' ')}
                                >
                                    <div className="sb-submission-step-icon">
                                        <FontAwesomeIcon icon={isComplete ? ['fas', 'check'] : (STATUS_ICONS[status.code] ?? ['fas', 'circle'])} />
                                    </div>
                                    <div className="sb-submission-step-label">{stepLabel}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Action panel — content varies by edit mode; omitted when empty */}
                {hasActionPanel && <div className="sb-submission-action-col">
                    {editMode ? (
                        /* In edit mode: show progress */
                        <>
                            {preSubmitStatus && totalCount > 0 && (
                                <div className="sb-submission-progress">
                                    <span className="sb-submission-progress-count">{completedCount}/{totalCount}</span>
                                    <span className="sb-submission-progress-label">Required Sections Completed</span>
                                </div>
                            )}
                            {preSubmitStatus && completedCount === totalCount && totalCount > 0 && (
                                <p className="text-muted small mb-0">All sections complete — save to submit.</p>
                            )}
                        </>
                    ) : (
                        /* View mode: normal status actions */
                        <>
                            {showProgress && totalCount > 0 && (
                                <div className="sb-submission-progress">
                                    <span className="sb-submission-progress-count">{completedCount}/{totalCount}</span>
                                    <span className="sb-submission-progress-label">Required Sections Completed</span>
                                </div>
                            )}

                            {showPrerequisiteNudge ? (
                                /* Prerequisite nudge: previous phase survey not yet accepted */
                                <div className="sb-submission-prerequisite">
                                    <p className="text-muted small mb-1">
                                        Complete the {prevSurveyType.name} Survey first.
                                    </p>
                                    <Button variant="outline-primary" onClick={() => changeTool(prevSurveyType.code)}>
                                        Go to {prevSurveyType.name}
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    {/* Keyed to the DISPLAYED status, not the stored one: the stored
                                        pre-submit status only refreshes on answer save, so it can sit
                                        at in-progress while every section is complete (the survey
                                        template changed underneath it). The stepper already shows
                                        ready-to-submit in that state; the action must agree with it.
                                        The displayed status is derived from the SAVED answers, which
                                        is what the submit endpoint recomputes from before it gates,
                                        so offering the button here is safe. */}
                                    {(editAction === 'start' || editAction === 'continue') && (
                                        <Button variant="primary" onClick={onEnterEditMode}>
                                            <FontAwesomeIcon icon={['fal', 'pencil']} />{' '}
                                            {editAction === 'start' ? 'Start Survey' : 'Continue the Survey'}
                                        </Button>
                                    )}

                                    {/* The page can hold answer values the server does not have
                                        while nobody has edited (a contact input selected, re-pointed
                                        or re-graded a contact on load). A submit would act on the
                                        stored values, not the ones on screen, so the editor is held
                                        until they agree: they open the survey, review what changed
                                        (a re-graded contact may leave it incomplete), save, then
                                        submit. A viewer who cannot edit gets no Submit at all. */}
                                    {submitAction === 'review' && (
                                        <Button variant="primary" disabled>
                                            Review changes
                                        </Button>
                                    )}
                                    {submitAction === 'submit' && (
                                        <Button variant="primary" onClick={() => setShowSubmitDialog(true)}>
                                            Submit for Review
                                        </Button>
                                    )}

                                    {editAction === 'edit' && (
                                        <Button variant="outline-secondary" onClick={onEnterEditMode}>
                                            <FontAwesomeIcon icon={['fal', 'pencil']} />{' '}Edit Survey
                                        </Button>
                                    )}

                                    {/* Shown at in-progress as well as ready: there the counter can
                                        read complete beside an In Progress stepper, and this is what
                                        explains it. Never at not-started — nothing was saved yet. */}
                                    {showUnsavedHint && (
                                        <p className="text-muted small mb-1">
                                            The answers shown differ from the last save. Edit the survey to review and save them.
                                        </p>
                                    )}
                                </>
                            )}

                            {currentStatusCode === 'in-review' && !canReviewSubmissions && (
                                <p className="text-muted small mb-1">
                                    Withdraw your submission to make changes.
                                </p>
                            )}
                            {currentStatusCode === 'in-review' && (
                                <>
                                    <Button variant="outline-secondary" onClick={() => setShowUnsubmitDialog(true)}>
                                        {canReviewSubmissions ? 'Return Survey' : 'Withdraw Submission'}
                                    </Button>
                                    {canReviewSubmissions && (
                                        <Button variant="success" onClick={() => setShowAcceptDialog(true)}>
                                            Accept Survey
                                        </Button>
                                    )}
                                </>
                            )}

                            {currentStatusCode === 'review-complete' && (
                                <>
                                    {/* On the last phase there is no next-phase button and a
                                        supplier has no actions, so without this the panel is
                                        blank at the one point the survey is actually done. */}
                                    <p className="text-muted small mb-1">
                                        <FontAwesomeIcon icon={['fas', 'check-circle']} className="text-success" />{' '}
                                        Review accepted — this survey is complete.
                                    </p>
                                    {canReviewSubmissions && (
                                        <Button variant="outline-secondary" onClick={() => setShowUnsubmitDialog(true)}>
                                            Reopen Review
                                        </Button>
                                    )}
                                    {nextSurveyType && (
                                        <Button variant="primary" onClick={() => changeTool(nextSurveyType.code)}>
                                            Continue to {nextSurveyType.name}
                                        </Button>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>}
            </div>
        </div>
    );
};
